<template>
    <div>
        <!-- Champ ancien password -->
        <b-form-group v-if="EnableOldPassword" :label="ShowLabel ? $t('OldPassword') : ''" :label-for="$id('oldPassword_field')" :label-cols="ShowLabel ? '5' : '0'">
            <b-input-group :id="$id('oldPassword_field')">
                <b-form-input v-model="oldPassword" :type="showOldPassword ? 'text': 'password'" :placeholder="$t('PasswordPlaceholder')" tabindex="1" autocomplete="off"/>
                <template #append>
                    <b-input-group-text>
                        <b-link @click="showOldPassword = !showOldPassword" v-b-tooltip.hover :title="$t('ShowPassword')"> 
                            <b-icon :icon="showOldPassword ? 'eye-slash-fill': 'eye-fill'" variant="black"/>
                        </b-link>
                    </b-input-group-text>
                </template>
            </b-input-group>
        </b-form-group>
        
        <!-- Bare de séparation -->
        <hr v-if="ShowSplitBar" class="pt-0 pb-0 pr-0 pt-2" />
        
        <!-- Champ nouveau mot de passe -->
        <b-form-group :label="ShowLabel ? $t('NewPassword') : ''" :label-for="$id('firstPassword_field')" :label-cols="ShowLabel ? '5' : '0'">
            <b-input-group :id="$id('firstPassword_field')">
                <b-form-input  :debounce="100" v-model="firstPassword" :type="showNewPassword ? 'text' : 'password'" :state="isSameAndValid" tabindex="2"
                    :placeholder="$t('PasswordPlaceholder')" autocomplete="new-password"/>
                <template #append>
                    <b-input-group-text>
                        <b-link @click="showNewPassword = !showNewPassword" v-b-tooltip.hover :title="$t('ShowPassword')"> 
                            <b-icon :icon="showNewPassword ? 'eye-slash-fill': 'eye-fill'" variant="black"/>
                        </b-link>
                    </b-input-group-text>
                </template>
            </b-input-group>
        </b-form-group>

        <!-- Champ de confirmation du mot de passe -->
        <b-form-group :label="ShowLabel ? $t('ConfirmPassword') : ''" :label-for="$id('secondPassword_field')" :label-cols="ShowLabel ? '5' : '0'"  >
            <b-input-group :id="$id('secondPassword_field')" >
                <b-form-input v-model="confirmPassword" :debounce="100" :type="showConfirmPassword ? 'text' : 'password'" :state="isSameAndValid" tabindex="3"
                    :placeholder="$t('ConfirmPasswordPlaceholder')" autocomplete="new-password"/>
                <template #append>
                    <b-input-group-text>
                        <b-link @click="showConfirmPassword = !showConfirmPassword" v-b-tooltip.hover :title="$t('ShowPassword')"> 
                            <b-icon :icon="showConfirmPassword ? 'eye-slash-fill': 'eye-fill'" variant="black"/>
                        </b-link>
                    </b-input-group-text>
                </template>
            </b-input-group>
            <b-form-invalid-feedback :state="isSame">
                {{ $t('PasswordNotEqual') }}
            </b-form-invalid-feedback>
        </b-form-group>

        <!-- Règles de sécurités imposés pour le mot de passe -->
        <b-container v-if="HasPasswordPolicy" >
            <!-- Règles mot de passe -->
            <b-row class="text-muted font-italic" >
                <b-col class="d-flex flex-wrap align-items-center "  :class="[{ 'text-danger': isRightLength == false, 'text-success-dark': isRightLength == true}]">
                    <b-icon :icon="!isRightLength? 'x-circle-fill' : 'check-circle-fill'" />
                    <span class="pl-2">{{ $tc('AtLeastN_Char', MinCharLength, { count: MinCharLength }) }} </span>
                </b-col>
            </b-row>
            <b-row class="text-muted font-italic" >
                <b-col class="d-flex flex-wrap align-items-center " :class="[{ 'text-danger': hasUpperCase == false, 'text-success-dark': hasUpperCase == true}]">
                    <b-icon :icon="!hasUpperCase? 'x-circle-fill' : 'check-circle-fill'" />
                    <span class="pl-2">{{ $tc('AtLeastN_Upper', MinUpperCase, { count: MinUpperCase }) }} </span>
                </b-col>
            </b-row>
            <b-row class="text-muted font-italic" >
                <b-col class="d-flex flex-wrap align-items-center " :class="[{ 'text-danger': hasNumber == false, 'text-success-dark': hasNumber == true}]">
                    <b-icon :icon="!hasNumber? 'x-circle-fill' : 'check-circle-fill'" />
                    <span class="pl-2">{{ $tc('AtLeastN_Digit', MinDigit, { count: MinDigit }) }} </span>
                </b-col>
            </b-row>
            <b-row class="text-muted font-italic" >
                <b-col class="d-flex flex-wrap align-items-center " :class="[{ 'text-danger': hasSpecialCaracter == false, 'text-success-dark': hasSpecialCaracter == true}]">
                    <b-icon :icon="!hasSpecialCaracter? 'x-circle-fill' : 'check-circle-fill'" />
                    <span class="pl-2">{{ $tc('AtLeastN_SpecialCharacter', MinSpecialChar, { count: MinSpecialChar, specialCharList: ListSpecialChar }) }} </span>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>
<script lang="ts">

import { BFormGroup } from 'bootstrap-vue';
import { Component,  Model, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import locales from './locales/PasswordInput.json'

/** Composant pour l'inscription des utilisateurs */
@Component({
    components:{
    },
    i18n: { messages: locales }
})
export default class PasswordInput extends Vue{

    /** Active la demande l'obligation de fournir l'ancien mot de passe */
    @Prop({default: () => false})
    public EnableOldPassword!: boolean

    /** Affiche la bar de séparation entre old et le bloc de saisi du mot de passe */
    @Prop({ default: () => false })
    public ShowSplitBar!: boolean

    /** Affiche les labels des champs */
    @Prop({ default: () => false })
    public ShowLabel!: boolean

    /** v-model contenant le mot de passe saisi */
    @Model("PasswordChangedEvent", { default: () => "" } )
    public Password!: string;

    /** Ancien mot de passe à saisir si activé */
    @PropSync("OldPassword",{ default: () => "" })
    public oldPassword!: string;

    /** Détermine si la politique de validation doit s'appliquer */
    @Prop({default: () => true})
    public HasPasswordPolicy: boolean;

    /** Minimum de caractère necessaire pour valider le mot de passe */
    @Prop({default: () => 8})
    public MinCharLength!:number;
    
    /** Minimun de lettre majuscule pour valider le mot de passe */
    @Prop({default: () => 1})
    public MinUpperCase!:number;
    
    /** Minimum de chiffre pour valider le mot de passe */
    @Prop({default: () => 1})
    public MinDigit!:number;
    
    /** Minimum de caractère spéciaux pour valider le mot de passe */
    @Prop({default: () => 1})
    public MinSpecialChar!: number;
    
    /** Liste de caractère spéciaux autorisé */
    @Prop({default: () => ".+-_*#?!@"} )
    public ListSpecialChar!: string;

    /** 1er champ password  */
    private firstPassword: string = "";
    
    /** 2nd champ password de confirmation du 1er */
    private confirmPassword: string = "";

    /** Observe les changements sur les champs 'mot de passe' */
    @Watch('firstPassword')
    @Watch('confirmPassword')
    private onChangePasswords(){

        let valid: boolean = true;
        if(this.HasPasswordPolicy){
            /** Est-ce qu'il y a des données inscrite */
            if(this.firstPassword.length > 0)
            {   

                /** Valide le nombre de caractères */
                this.isRightLength = this.firstPassword.length >= this.MinCharLength;
                
                /** Valide le nombre de majuscule */
                const fpUppersFound = this.firstPassword.match(/[A-Z]/g)?.length;
                this.hasUpperCase = fpUppersFound ? (fpUppersFound >= this.MinUpperCase) : false;

                /** Valide le nombre de chiffre */
                const fpDigitFound = this.firstPassword.match(/\d/g)?.length;
                this.hasNumber = fpDigitFound ? (fpDigitFound >= this.MinDigit) : false;

                /** Valide le nombre caractère spécial */
                const fpSpecialCharFound = Array.from(this.firstPassword).filter(c => this.ListSpecialChar.includes(c)).length;
                this.hasSpecialCaracter = fpSpecialCharFound && (fpSpecialCharFound >= this.MinDigit);

            }else{
                this.isRightLength = null;
                this.hasUpperCase = null;
                this.hasNumber = null;
                this.hasSpecialCaracter = null;
            }
            valid = this.isRightLength && this.hasUpperCase && this.hasNumber && this.hasSpecialCaracter;
        }

        this.isSameAndValid = this.confirmPassword.length > 0 ? ( valid && this.firstPassword === this.confirmPassword) : null
        this.isSame = this.confirmPassword.length > 0 ? ( this.firstPassword === this.confirmPassword) : null;
        
        if(valid && this.isSameAndValid)
            this.$emit('PasswordChangedEvent', this.firstPassword)
        else
            this.$emit('PasswordChangedEvent', "")
    }

    /** Longeur du mot de passe suffisant 
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private isRightLength : boolean = null;

    /** Contient une majuscule
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private hasUpperCase : boolean = null;

    /** Contient un chiffre 
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private hasNumber : boolean = null;

    /** Contient un caractère spécial 
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private hasSpecialCaracter : boolean = null;

    /** 
     * les deux mot de passes sont identiques 
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private isSame : boolean = null;

    /**
     * les deux mot de passes sont identiques et validé celon les critères de sécurités 
     * @description (est à null par défaut pour pour affichage uniquement au moment de la saisie)
     */
    private isSameAndValid: boolean = null;


    /** Affiche l'ancien password en clair*/
    private showOldPassword: boolean = false;

    /** Affiche le nouveau password en clair*/
    private showNewPassword: boolean = false;

    /** Affiche le mot de passe de confirmation */
    private showConfirmPassword: boolean = false;

}
</script>
<style scoped>
</style>